
import Phaser from 'phaser';
import { deckService } from '../services/DeckService';
import * as AudioService from '@/services/AudioService';
import { getAssetUrl, cdnAssetUrl } from '../lib/assets';
import { createAllAnimations } from '../game/animations/animationLoader';

export class PreloadScene extends Phaser.Scene {
  private loadingText!: Phaser.GameObjects.Text;
  private progressBar!: Phaser.GameObjects.Graphics;
  private progressBox!: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: 'PreloadScene' });
  }

  preload(): void {
    this.createLoadingUI();
    this.loadAssets();
    this.setupLoadEvents();
  }

  private createLoadingUI(): void {
    const { width, height } = this.cameras.main;

    // Progress bar background
    this.progressBox = this.add.graphics();
    this.progressBox.fillStyle(0x222244, 0.8);
    this.progressBox.fillRect(width / 4, height / 2 - 15, width / 2, 30);

    // Progress bar
    this.progressBar = this.add.graphics();

    // Loading text
    this.loadingText = this.add.text(width / 2, height / 2 - 50, 'Loading...', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  private loadAssets(): void {
    // =========================================================================
    // Load Oryx Tiny Dungeon Spritesheets (16x16 tiles)
    // =========================================================================

    // World tiles: floors, walls, doors, props (17 columns, ~16 rows)
    this.load.spritesheet('world', 'assets/oryx_tiny_dungeon/tiny_dungeon_world.png', {
      frameWidth: 16,
      frameHeight: 16,
    });

    // Monsters: all creatures with 4-direction animations (29 columns, ~16 rows)
    this.load.spritesheet('monsters', 'assets/oryx_tiny_dungeon/tiny_dungeon_monsters.png', {
      frameWidth: 16,
      frameHeight: 16,
    });

    // Audio assets
    this.load.audio('courtroom', 'assets/courtroom.ogg');
    this.load.audio('judgment', 'assets/Judgement.ogg');
    this.load.audio('correct', 'assets/Judgement.ogg');
    this.load.audio('wrong', 'assets/Judgement.ogg');

    // Items: weapons, armor, potions, keys, etc (23 columns, ~4 rows)
    this.load.spritesheet('items', 'assets/oryx_tiny_dungeon/tiny_dungeon_items.png', {
      frameWidth: 16,
      frameHeight: 16,
    });

    // Interface: panels, buttons, hearts, bars (variable layout)
    this.load.spritesheet('interface', 'assets/oryx_tiny_dungeon/tiny_dungeon_interface.png', {
      frameWidth: 16,
      frameHeight: 16,
    });

    // FX: projectiles, impacts, sparks, etc
    this.load.spritesheet('fx', 'assets/oryx_tiny_dungeon/tiny_dungeon_fx.png', {
      frameWidth: 16,
      frameHeight: 16,
    });

    // Font spritesheet for pixel text
    this.load.spritesheet('font', 'assets/oryx_tiny_dungeon/tiny_dungeon_font.png', {
      frameWidth: 8,
      frameHeight: 8,
    });

    // Portraits: larger character portraits (24x24)
    this.load.spritesheet('portraits', 'assets/oryx_tiny_dungeon/tiny_dungeon_portraits.png', {
      frameWidth: 24,
      frameHeight: 24,
    });

    // Kim Portraits (160x160)
    this.load.atlas('kim-portraits', 'assets/kim_sprite/kim_portraits_oryx_style_160.png', 'assets/kim_sprite/kim_portraits_oryx_style_160.json');

    // Kim Walking Spritesheet (Placeholder: using copy of monsters sheet)
    // TODO: When user provides custom 16x16 sheet, update frame config (likely 0-7)
    this.load.spritesheet('kim-walk', 'assets/kim_sprite/kim_walk.png', {
      frameWidth: 16,
      frameHeight: 16,
    });

    // =========================================================================
    // Outdoor/Grass Tilesets for exterior scenes
    // =========================================================================
    // Grass tileset for outdoor areas (32x32 tiles)
    this.load.image('tileset-grass', 'assets/Pixel Art Top Down - Basic v1.2.2/Texture/TX Tileset Grass.png');
    this.load.image('tileset-props', 'assets/Pixel Art Top Down - Basic v1.2.2/Texture/TX Props.png');
    this.load.image('tileset-stone', 'assets/Pixel Art Top Down - Basic v1.2.2/Texture/TX Tileset Stone Ground.png');

    // =========================================================================
    // Sky Backgrounds for exterior scenes
    // =========================================================================
    this.load.image('sky-noon', 'assets/backgrounds/noonbackground.png');
    this.load.image('sky-dawn', 'assets/backgrounds/dawnbackground.png');
    this.load.image('sky-sunset', 'assets/backgrounds/sunsetbackground.png');
    this.load.image('sky-night', 'assets/backgrounds/nightbackgroundwithmoon.png');

    // =========================================================================
    // Prettier grass tile variants for exterior scenes (CDN-hosted)
    // =========================================================================
    this.load.image('grass-a', cdnAssetUrl('assets/nature/td_world_floor_grass_a.png'));
    this.load.image('grass-b', cdnAssetUrl('assets/nature/td_world_floor_grass_b.png'));
    this.load.image('grass-c', cdnAssetUrl('assets/nature/td_world_floor_grass_c.png'));
    this.load.image('grass-d', cdnAssetUrl('assets/nature/td_world_floor_grass_d.png'));
    this.load.image('grass-e', cdnAssetUrl('assets/nature/td_world_floor_grass_e.png'));
    this.load.image('grass-f', cdnAssetUrl('assets/nature/td_world_floor_grass_f.png'));
    this.load.image('grass-detail-a', cdnAssetUrl('assets/nature/td_world_grass_a.png'));
    this.load.image('grass-detail-b', cdnAssetUrl('assets/nature/td_world_grass_b.png'));
    this.load.image('grass-detail-c', cdnAssetUrl('assets/nature/td_world_grass_c.png'));
    this.load.image('grass-detail-d', cdnAssetUrl('assets/nature/td_world_grass_d.png'));
    this.load.image('grass-detail-e', cdnAssetUrl('assets/nature/td_world_grass_e.png'));
    this.load.image('grass-path-a', cdnAssetUrl('assets/nature/td_world_floor_grass_path_a.png'));
    this.load.image('grass-path-b', cdnAssetUrl('assets/nature/td_world_floor_grass_path_b.png'));
    
    // Supreme Court backdrop - load as image (atlas grid too large for WebGL)
    this.load.image('supreme_court_backdrop', 'assets/_generated/supreme_court_with_sign/slices/supreme_court_with_sign_0001.png');

    // =========================================================================
    // Generated Atlas Packs (cleaned up - removed confusing names)
    // =========================================================================
    const packs = [
      'a_weapons_discovery',
      'b_clothing_discovery',
      'c_evil_critters',
      // 'default_setting_and_background_items', // Texture too large for WebGL
      // 'extra_settings_and_items_library_courthouse_river_desks_writing', // Texture too large for WebGL
      'friendly_npcs_default_sparksheet',
      'friendly_npcs_textbox_spritesheet',
      'k1_kim_with_clothing_and_weapons',
      'k1_kim_with_noclothing_and_noweapons',
      'k1_kim_with_noclothing_and_weapons',
      'legal_items_high_res',
      'legal_menu_icons',
      'legal_menu_icons_9b',
      'legal_rpg_vfx_and_projectiles_spritesheet',
      'legal_rpg_vfx_and_projectiles_spritesheet_8b',
      'legalitems_20',
      'supreme_court_spritesheet',
      // Kim's Supreme Court Arrival assets
      'crowd_media_parade',
      'djt_leader',
      'jd_follower',
      'limo',
      'supreme_court_no_sign',
      // 'supreme_court_with_sign', // Grid too large (45184px) for WebGL - load as image instead
      'trees',
    ];

    packs.forEach(pack => {
      this.load.atlas(
        pack,
        `assets/_generated/${pack}/${pack}_grid.png`,
        `assets/_generated/${pack}/${pack}_atlas.json`
      );
    });

    // =========================================================================
    // Load individual sliced sprites for specific use cases
    // =========================================================================

    // Load some key individual sprites for easier access via the Asset Index
    const dynamicAssets = [
      { key: 'chest', assetKey: 'CHEST_CLOSED', group: 'WORLD_TILES' },
      { key: 'chest-open', assetKey: 'CHEST_OPEN', group: 'WORLD_TILES' },
      { key: 'door-closed', assetKey: 'DOOR_STONE_V_CLOSED', group: 'WORLD_TILES' },
      { key: 'door-open', assetKey: 'DOOR_STONE_V_OPEN', group: 'WORLD_TILES' },
    ] as const;

    dynamicAssets.forEach(({ key, assetKey, group }) => {
      const url = getAssetUrl(group, assetKey);
      if (url) {
        this.load.image(key, url);
      } else {
        console.warn(`[PreloadScene] Failed to resolve asset URL for ${group}.${assetKey}`);
      }
    });

    // =========================================================================
    // Load hub map and game data
    // =========================================================================
    this.load.json('hub-map', 'data/hub-map.json');
    this.load.json('deck', 'data/flashcards.json');
    this.load.json('justice-quotes', 'data/scotus_justices_quotes.json');

    // =========================================================================
    // Load dungeon data for each subject
    // =========================================================================
    const dungeonIds = [
      'agency',
      'civil-procedure',
      'con-law',
      'conflicts-of-law',
      'contracts',
      'corporations',
      'criminal-law',
      'criminal-procedure',
      'evidence',
      'family-law',
      'partnerships',
      'real-property',
      'secured-transactions',
      'torts',
      'trusts-and-estates',
    ];
    for (const id of dungeonIds) {
      this.load.json(`dungeon-${id}`, `data/dungeons/${id}.json`);
    }
    this.load.json('dungeon-index', 'data/dungeons/index.json');

    // =========================================================================
    // Load audio
    // =========================================================================
    // Audio already loaded above; avoid duplicate keys which can be confusing
    // for the loader. Keep audio loads centralized earlier in this method.
  }

  private setupLoadEvents(): void {
    const { width, height } = this.cameras.main;

    this.load.on('progress', (value: number) => {
      this.progressBar.clear();
      this.progressBar.fillStyle(0x4488ff, 1);
      this.progressBar.fillRect(
        width / 4 + 5,
        height / 2 - 10,
        (width / 2 - 10) * value,
        20
      );
      this.loadingText.setText(`Loading... ${Math.round(value * 100)}%`);
    });

    this.load.on('complete', () => {
      this.loadingText.setText('Initializing...');
    });

    // Log and continue on file load errors - don't block the entire game
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`[PreloadScene] Failed to load: ${file.key} (${file.url})`);
    });
  }

  async create(): Promise<void> {
    console.info('[PreloadScene] create() started');
    try {
      // Initialize deck service with pre-loaded data from Phaser cache
      console.info('[PreloadScene] Loading deck...');
      const cachedDeck = this.cache.json.get('deck');
      await deckService.loadDeck(cachedDeck);
      console.info(`[PreloadScene] Deck loaded: ${deckService.getTotalCards()} cards`);

      // Create animations
      console.info('[PreloadScene] Creating animations...');
      this.createAnimations();
      console.info('[PreloadScene] Animations created');

      // Clean up loading UI
      this.progressBar.destroy();
      this.progressBox.destroy();
      this.loadingText.destroy();

      // Initialize audio service for Phaser
      try {
        AudioService.init(this as any);

        // Listen for settings-driven audio toggles
        window.addEventListener('audio-toggle', (_e: Event) => {
          const enabled = (_e as CustomEvent).detail?.enabled;
          if (typeof enabled === 'boolean') AudioService.setMuted(enabled);
        });

        window.addEventListener('audio-volume', (_e: Event) => {
          const vol = (_e as CustomEvent).detail?.volume;
          if (typeof vol === 'number') AudioService.setVolume(vol);
        });
      } catch {
        // AudioService may be a no-op in test env
      }

        // Expose E2E test mode flag when '?e2e' or '?test' query param present
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get('e2e') || params.get('test')) {
            (window as any).__E2E_TEST_MODE = true;
          }
        } catch {
          // ignore
        }

      // Start limo arrival scene (intro cutscene)
      console.info('[PreloadScene] Starting LimoArrivalScene...');
      this.scene.start('LimoArrivalScene');
      console.info('[PreloadScene] LimoArrivalScene started');
    } catch (error) {
      console.error('[PreloadScene] Failed to initialize:', error);
      this.loadingText.setText('Failed to load game data.\nPlease run: npm run sync');
      this.loadingText.setColor('#ff4444');
    }
  }

  /**
   * Create all game animations from spritesheets
   */
  private createAnimations(): void {
    // Wrap animation creation in try/catch - missing textures should not crash the game
    const safeAnim = (key: string, config: Phaser.Types.Animations.Animation) => {
      try {
        this.anims.create(config);
      } catch (e) {
        console.warn(`[PreloadScene] Failed to create animation '${key}':`, e);
      }
    };

    // =========================================================================
    // Player/Hero Animations (Knight class - frames 16-23)
    // Each direction has 2 frames for walk cycle
    // =========================================================================

    // Knight walking animations
    safeAnim('player-walk-down', {
      key: 'player-walk-down',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [16, 17] }),
      frameRate: 6,
      repeat: -1,
    });

    safeAnim('player-walk-left', {
      key: 'player-walk-left',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [18, 19] }),
      frameRate: 6,
      repeat: -1,
    });

    safeAnim('player-walk-right', {
      key: 'player-walk-right',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [20, 21] }),
      frameRate: 6,
      repeat: -1,
    });

    safeAnim('player-walk-up', {
      key: 'player-walk-up',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [22, 23] }),
      frameRate: 6,
      repeat: -1,
    });

    // Player idle (single frame from down-facing)
    safeAnim('player-idle', {
      key: 'player-idle',
      frames: [{ key: 'monsters', frame: 16 }],
      frameRate: 1,
    });

    // =========================================================================
    // Character Specific Animations
    // =========================================================================

    // Kim using high-res atlas 'k1_kim_with_noclothing_and_noweapons'
    // Atlas has 60 frames (0001-0060) at 192x272 pixels - various poses/actions
    // Frame mapping (assumed layout based on typical sprite sheets):
    // 0001-0010: Idle/standing poses (front facing variations)
    // 0011-0020: Walking/movement poses
    // 0021-0030: Action/attack poses
    // 0031-0040: Casting/special ability poses
    // 0041-0050: Victory/celebration poses
    // 0051-0060: Hurt/reaction poses
    const kimAtlas = 'k1_kim_with_noclothing_and_noweapons';

    // Kim idle animation (subtle breathing loop using first few frames)
    safeAnim('kim-idle', {
      key: 'kim-idle',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0001' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0002' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0003' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0002' },
      ],
      frameRate: 3,
      repeat: -1,
    });

    // Kim walk animations (using movement frames)
    safeAnim('kim-walk-down', {
      key: 'kim-walk-down',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0011' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0012' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0013' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0014' },
      ],
      frameRate: 6,
      repeat: -1,
    });

    safeAnim('kim-walk-left', {
      key: 'kim-walk-left',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0015' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0016' },
      ],
      frameRate: 6,
      repeat: -1,
    });

    safeAnim('kim-walk-right', {
      key: 'kim-walk-right',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0017' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0018' },
      ],
      frameRate: 6,
      repeat: -1,
    });

    safeAnim('kim-walk-up', {
      key: 'kim-walk-up',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0019' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0020' },
      ],
      frameRate: 6,
      repeat: -1,
    });

    // Kim action animations (for encounters)
    safeAnim('kim-attack', {
      key: 'kim-attack',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0021' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0022' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0023' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0024' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0025' },
      ],
      frameRate: 10,
      repeat: 0,
    });

    safeAnim('kim-cast', {
      key: 'kim-cast',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0031' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0032' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0033' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0034' },
      ],
      frameRate: 8,
      repeat: 0,
    });

    safeAnim('kim-victory', {
      key: 'kim-victory',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0041' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0042' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0043' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0044' },
      ],
      frameRate: 6,
      repeat: 0,
    });

    safeAnim('kim-hurt', {
      key: 'kim-hurt',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0051' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0052' },
      ],
      frameRate: 6,
      repeat: 0,
    });

    safeAnim('kim-defeat', {
      key: 'kim-defeat',
      frames: [
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0055' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0056' },
        { key: kimAtlas, frame: 'k1_kim_with_noclothing_and_noweapons_0057' },
      ],
      frameRate: 4,
      repeat: 0,
    });

    // =========================================================================
    // DJT Animations (djt_leader atlas - 23 frames at 224x232)
    // =========================================================================
    const djtAtlas = 'djt_leader';
    if (this.textures.exists(djtAtlas)) {
      safeAnim('djt-idle', {
        key: 'djt-idle',
        frames: [
          { key: djtAtlas, frame: 'djt_leader_0001' },
          { key: djtAtlas, frame: 'djt_leader_0002' },
        ],
        frameRate: 3,
        repeat: -1,
      });
      safeAnim('djt-walk', {
        key: 'djt-walk',
        frames: [
          { key: djtAtlas, frame: 'djt_leader_0001' },
          { key: djtAtlas, frame: 'djt_leader_0002' },
          { key: djtAtlas, frame: 'djt_leader_0003' },
          { key: djtAtlas, frame: 'djt_leader_0004' },
        ],
        frameRate: 6,
        repeat: -1,
      });
      safeAnim('djt-gesture', {
        key: 'djt-gesture',
        frames: [
          { key: djtAtlas, frame: 'djt_leader_0005' },
          { key: djtAtlas, frame: 'djt_leader_0006' },
        ],
        frameRate: 4,
        repeat: -1,
      });
    }

    // =========================================================================
    // JD Animations (jd_follower atlas - 22 frames at 216x232)
    // =========================================================================
    const jdAtlas = 'jd_follower';
    if (this.textures.exists(jdAtlas)) {
      safeAnim('jd-idle', {
        key: 'jd-idle',
        frames: [
          { key: jdAtlas, frame: 'jd_follower_0001' },
          { key: jdAtlas, frame: 'jd_follower_0002' },
        ],
        frameRate: 3,
        repeat: -1,
      });
      safeAnim('jd-walk', {
        key: 'jd-walk',
        frames: [
          { key: jdAtlas, frame: 'jd_follower_0001' },
          { key: jdAtlas, frame: 'jd_follower_0002' },
          { key: jdAtlas, frame: 'jd_follower_0003' },
          { key: jdAtlas, frame: 'jd_follower_0004' },
        ],
        frameRate: 6,
        repeat: -1,
      });
      safeAnim('jd-phone', {
        key: 'jd-phone',
        frames: [
          { key: jdAtlas, frame: 'jd_follower_0008' },
          { key: jdAtlas, frame: 'jd_follower_0009' },
        ],
        frameRate: 3,
        repeat: -1,
      });
    }

    // =========================================================================
    // Limo Animation (limo atlas - 65 frames at 136x104, driving animation)
    // =========================================================================
    const limoAtlas = 'limo';
    if (this.textures.exists(limoAtlas)) {
      // Create driving animation using all 65 frames
      const limoFrames: Phaser.Types.Animations.AnimationFrame[] = [];
      for (let i = 1; i <= 65; i++) {
        const frameNum = i.toString().padStart(4, '0');
        limoFrames.push({ key: limoAtlas, frame: `limo_${frameNum}` });
      }

      safeAnim('limo-drive', {
        key: 'limo-drive',
        frames: limoFrames,
        frameRate: 12,
        repeat: -1,
      });

      // Idle animation (just first few frames for subtle movement)
      safeAnim('limo-idle', {
        key: 'limo-idle',
        frames: [
          { key: limoAtlas, frame: 'limo_0001' },
          { key: limoAtlas, frame: 'limo_0002' },
          { key: limoAtlas, frame: 'limo_0003' },
        ],
        frameRate: 4,
        repeat: -1,
      });
    }

    // =========================================================================
    // Supreme Court Justice Animations (supreme_court_spritesheet - 7 frames each, 208x232)
    // =========================================================================
    const justiceAtlas = 'supreme_court_spritesheet';
    if (this.textures.exists(justiceAtlas)) {
      const justices = [
        { id: 'john_roberts', start: 1 },
        { id: 'clarence_thomas', start: 8 },
        { id: 'samuel_alito', start: 15 },
        { id: 'sonia_sotomayor', start: 22 },
        { id: 'elena_kagan', start: 29 },
        { id: 'neil_gorsuch', start: 36 },
        { id: 'brett_kavanaugh', start: 43 },
        { id: 'amy_coney_barrett', start: 50 },
        { id: 'ketanji_brown_jackson', start: 57 },
      ];

      justices.forEach(({ id, start }) => {
        const pad = (n: number) => String(n).padStart(4, '0');
        
        // Idle animation (first 2 frames)
        safeAnim(`${id}-idle`, {
          key: `${id}-idle`,
          frames: [
            { key: justiceAtlas, frame: `supreme_court_spritesheet_${pad(start)}` },
            { key: justiceAtlas, frame: `supreme_court_spritesheet_${pad(start + 1)}` },
          ],
          frameRate: 2,
          repeat: -1,
        });

        // Speaking animation (frames 3-5)
        safeAnim(`${id}-speak`, {
          key: `${id}-speak`,
          frames: [
            { key: justiceAtlas, frame: `supreme_court_spritesheet_${pad(start + 2)}` },
            { key: justiceAtlas, frame: `supreme_court_spritesheet_${pad(start + 3)}` },
            { key: justiceAtlas, frame: `supreme_court_spritesheet_${pad(start + 4)}` },
          ],
          frameRate: 4,
          repeat: -1,
        });

        // Thinking/considering animation (frames 6-7)
        safeAnim(`${id}-think`, {
          key: `${id}-think`,
          frames: [
            { key: justiceAtlas, frame: `supreme_court_spritesheet_${pad(start + 5)}` },
            { key: justiceAtlas, frame: `supreme_court_spritesheet_${pad(start + 6)}` },
          ],
          frameRate: 2,
          repeat: -1,
        });
      });
    }

    // Knight (using 'monsters' texture)
    const knightBase = 16;
    safeAnim('knight-walk-down', { key: 'knight-walk-down', frames: this.anims.generateFrameNumbers('monsters', { frames: [knightBase, knightBase + 1] }), frameRate: 6, repeat: -1 });
    safeAnim('knight-walk-left', { key: 'knight-walk-left', frames: this.anims.generateFrameNumbers('monsters', { frames: [knightBase + 2, knightBase + 3] }), frameRate: 6, repeat: -1 });
    safeAnim('knight-walk-right', { key: 'knight-walk-right', frames: this.anims.generateFrameNumbers('monsters', { frames: [knightBase + 4, knightBase + 5] }), frameRate: 6, repeat: -1 });
    safeAnim('knight-walk-up', { key: 'knight-walk-up', frames: this.anims.generateFrameNumbers('monsters', { frames: [knightBase + 6, knightBase + 7] }), frameRate: 6, repeat: -1 });
    safeAnim('knight-idle', { key: 'knight-idle', frames: [{ key: 'monsters', frame: knightBase }], frameRate: 1 });

    // Wizard (using 'monsters' texture)
    const wizardBase = 116;
    safeAnim('wizard-walk-down', { key: 'wizard-walk-down', frames: this.anims.generateFrameNumbers('monsters', { frames: [wizardBase, wizardBase + 1] }), frameRate: 6, repeat: -1 });
    safeAnim('wizard-walk-left', { key: 'wizard-walk-left', frames: this.anims.generateFrameNumbers('monsters', { frames: [wizardBase + 2, wizardBase + 3] }), frameRate: 6, repeat: -1 });
    safeAnim('wizard-walk-right', { key: 'wizard-walk-right', frames: this.anims.generateFrameNumbers('monsters', { frames: [wizardBase + 4, wizardBase + 5] }), frameRate: 6, repeat: -1 });
    safeAnim('wizard-walk-up', { key: 'wizard-walk-up', frames: this.anims.generateFrameNumbers('monsters', { frames: [wizardBase + 6, wizardBase + 7] }), frameRate: 6, repeat: -1 });
    safeAnim('wizard-idle', { key: 'wizard-idle', frames: [{ key: 'monsters', frame: wizardBase }], frameRate: 1 });

    // Rogue (using 'monsters' texture)
    const rogueBase = 58;
    safeAnim('rogue-walk-down', { key: 'rogue-walk-down', frames: this.anims.generateFrameNumbers('monsters', { frames: [rogueBase, rogueBase + 1] }), frameRate: 6, repeat: -1 });
    safeAnim('rogue-walk-left', { key: 'rogue-walk-left', frames: this.anims.generateFrameNumbers('monsters', { frames: [rogueBase + 2, rogueBase + 3] }), frameRate: 6, repeat: -1 });
    safeAnim('rogue-walk-right', { key: 'rogue-walk-right', frames: this.anims.generateFrameNumbers('monsters', { frames: [rogueBase + 4, rogueBase + 5] }), frameRate: 6, repeat: -1 });
    safeAnim('rogue-walk-up', { key: 'rogue-walk-up', frames: this.anims.generateFrameNumbers('monsters', { frames: [rogueBase + 6, rogueBase + 7] }), frameRate: 6, repeat: -1 });
    safeAnim('rogue-idle', { key: 'rogue-idle', frames: [{ key: 'monsters', frame: rogueBase }], frameRate: 1 });

    // =========================================================================
    // Generic monster idle animations (for encounters)
    // These use frame pairs for subtle movement
    // =========================================================================

    // Zombie idle
    safeAnim('zombie-idle', {
      key: 'zombie-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [174, 175] }),
      frameRate: 3,
      repeat: -1,
    });

    // Demon idle
    safeAnim('demon-idle', {
      key: 'demon-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [232, 233] }),
      frameRate: 3,
      repeat: -1,
    });

    // Spider idle
    safeAnim('spider-idle', {
      key: 'spider-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [306, 307] }),
      frameRate: 4,
      repeat: -1,
    });

    // Bat idle
    safeAnim('bat-idle', {
      key: 'bat-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [298, 299] }),
      frameRate: 6,
      repeat: -1,
    });

    // Wolf idle
    safeAnim('wolf-idle', {
      key: 'wolf-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [314, 315] }),
      frameRate: 4,
      repeat: -1,
    });

    // Rat idle
    safeAnim('rat-idle', {
      key: 'rat-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [290, 291] }),
      frameRate: 5,
      repeat: -1,
    });

    // Dragon idle
    safeAnim('dragon-idle', {
      key: 'dragon-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [480, 481] }),
      frameRate: 3,
      repeat: -1,
    });

    // Wizard idle (monster version)
    safeAnim('monster-wizard-idle', {
      key: 'monster-wizard-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [116, 117] }),
      frameRate: 3,
      repeat: -1,
    });

    // Wraith idle
    safeAnim('wraith-idle', {
      key: 'wraith-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [198, 199] }),
      frameRate: 4,
      repeat: -1,
    });

    // Slime idle
    safeAnim('slime-idle', {
      key: 'slime-idle',
      frames: this.anims.generateFrameNumbers('monsters', { frames: [364, 365] }),
      frameRate: 3,
      repeat: -1,
    });

    // =========================================================================
    // Prop animations (torches, campfires, etc)
    // =========================================================================

    safeAnim('torch-flicker', {
      key: 'torch-flicker',
      frames: this.anims.generateFrameNumbers('world', { frames: [206, 207] }),
      frameRate: 6,
      repeat: -1,
    });

    safeAnim('campfire-burn', {
      key: 'campfire-burn',
      frames: this.anims.generateFrameNumbers('world', { frames: [200, 201] }),
      frameRate: 6,
      repeat: -1,
    });

    safeAnim('candle-flicker', {
      key: 'candle-flicker',
      frames: this.anims.generateFrameNumbers('world', { frames: [203, 204] }),
      frameRate: 4,
      repeat: -1,
    });

    safeAnim('cauldron-bubble', {
      key: 'cauldron-bubble',
      frames: this.anims.generateFrameNumbers('world', { frames: [198, 199] }),
      frameRate: 4,
      repeat: -1,
    });

    // =========================================================================
    // FX Animations
    // =========================================================================

    // Fire impact
    safeAnim('fx-fire-impact', {
      key: 'fx-fire-impact',
      frames: this.anims.generateFrameNumbers('fx', { frames: [36, 37, 38] }),
      frameRate: 12,
      repeat: 0,
    });

    // Ice impact
    safeAnim('fx-ice-impact', {
      key: 'fx-ice-impact',
      frames: this.anims.generateFrameNumbers('fx', { frames: [39, 40, 41] }),
      frameRate: 12,
      repeat: 0,
    });

    // Slash effect
    safeAnim('fx-slash', {
      key: 'fx-slash',
      frames: this.anims.generateFrameNumbers('fx', { frames: [64, 65, 66] }),
      frameRate: 15,
      repeat: 0,
    });

    // Sparkle effect (for correct answers)
    safeAnim('fx-sparkle', {
      key: 'fx-sparkle',
      frames: this.anims.generateFrameNumbers('fx', { frames: [84, 85, 86] }),
      frameRate: 10,
      repeat: 2,
    });

    // Smoke effect (for wrong answers)
    safeAnim('fx-smoke', {
      key: 'fx-smoke',
      frames: this.anims.generateFrameNumbers('fx', { frames: [87, 88] }),
      frameRate: 6,
      repeat: 3,
    });

    // Create animations from new asset packs
    createAllAnimations(this);

    console.info('Animations created successfully');
  }
}
